import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TransporterRegistrationUpload = ({ onUploadComplete }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const fileInputRef = useRef(null);

  const templateFields = [
    { field: 'Company Name', required: true, example: 'Fast Logistics Pvt Ltd' },
    { field: 'Registration Number', required: true, example: 'TRANS12345' },
    { field: 'GST Number', required: true, example: '29ABCDE1234F1Z5' },
    { field: 'PAN Number', required: true, example: 'ABCDE1234F' },
    { field: 'Company Type', required: true, example: 'Private Limited' },
    { field: 'Established Year', required: true, example: '2015' },
    { field: 'Registered Address', required: true, example: 'Plot 45, Industrial Area, Mumbai' },
    { field: 'City', required: true, example: 'Mumbai' },
    { field: 'State', required: true, example: 'Maharashtra' },
    { field: 'Pincode', required: true, example: '400001' },
    { field: 'Owner Name', required: true, example: 'Suresh Patel' },
    { field: 'Owner Contact', required: true, example: '+91 9876543210' },
    { field: 'Owner Email', required: true, example: 'owner@fastlogistics.com' },
    { field: 'Company Email', required: true, example: 'contact@fastlogistics.com' },
    { field: 'Company Phone', required: true, example: '+91 22-12345678' },
    { field: 'Fleet Size', required: true, example: '50' },
    { field: 'Service Areas', required: true, example: 'Maharashtra, Gujarat, Karnataka' },
    { field: 'Insurance Provider', required: true, example: 'National Insurance Company' },
    { field: 'Insurance Expiry', required: true, example: '2026-12-31' },
    { field: 'Certifications', required: false, example: 'ISO 9001, FSSAI Transport License' },
    { field: 'Bank Account Number', required: true, example: '12345678901234' },
    { field: 'Bank Name', required: true, example: 'State Bank of India' },
    { field: 'IFSC Code', required: true, example: 'SBIN0001234' },
    { field: 'Verification Documents (URLs)', required: false, example: 'http://example.com/gst.pdf, http://example.com/pan.pdf' }
  ];

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      validateFile(file);
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const file = e?.dataTransfer?.files?.[0];
    if (file) {
      validateFile(file);
    }
  };

  const validateFile = (file) => {
    const errors = [];
    
    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!allowedTypes?.includes(file?.type)) {
      errors?.push('Invalid file type. Please upload CSV or Excel (.xlsx) files only.');
    }
    
    if (file?.size > 10 * 1024 * 1024) {
      errors?.push('File size exceeds 10MB limit.');
    }
    
    if (errors?.length > 0) {
      setValidationErrors(errors);
      setSelectedFile(null);
    } else {
      setValidationErrors([]);
      setSelectedFile(file);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);
          
          const newHistoryEntry = {
            id: Date.now(),
            type: 'Transporter Registration',
            fileName: selectedFile?.name,
            uploadDate: new Date()?.toLocaleString('en-IN'),
            status: 'success',
            totalRecords: Math?.floor(Math?.random() * 100) + 20,
            successfulRecords: Math?.floor(Math?.random() * 95) + 18,
            failedRecords: Math?.floor(Math?.random() * 5),
            uploadedBy: 'Current User'
          };
          
          onUploadComplete?.(newHistoryEntry);
          setSelectedFile(null);
          
          return 100;
        }
        return prev + 10;
      });
    }, 300);
  };

  const handleDownloadTemplate = () => {
    alert('Downloading Transporter Registration template...\n\nThe template will include all company profile fields with example data.');
  };

  return (
    <div className="space-y-6">
      {/* Template Download Section */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Icon name="FileDown" size={24} className="text-purple-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-purple-900 mb-1">
                Download Transporter Registration Template
              </h3>
              <p className="text-sm text-purple-700 mb-3">
                Download the pre-formatted template for transporter company registration. 
                Include complete business details, verification documents, and fleet information.
              </p>
              <div className="bg-white rounded-lg p-3 mb-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">Template includes:</p>
                <ul className="text-xs text-gray-600 space-y-1 ml-4 list-disc">
                  <li><strong>Company Profile:</strong> Name, registration, GST, PAN details</li>
                  <li><strong>Business Info:</strong> Type, established year, service areas</li>
                  <li><strong>Contact Details:</strong> Owner and company contact information</li>
                  <li><strong>Fleet Information:</strong> Fleet size, insurance details</li>
                  <li><strong>Banking Details:</strong> Account number, IFSC, bank name</li>
                  <li><strong>Verification:</strong> Document URLs for business verification</li>
                </ul>
              </div>
            </div>
          </div>
          <Button
            onClick={handleDownloadTemplate}
            className="bg-purple-600 hover:bg-purple-700 text-white whitespace-nowrap"
          >
            <Icon name="Download" size={18} />
            <span>Download Template</span>
          </Button>
        </div>
      </div>

      {/* Upload Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
          isDragging
            ? 'border-purple-500 bg-purple-50' :'border-gray-300 bg-white hover:border-purple-500 hover:bg-purple-50/30'
        }`}
      >
        {selectedFile ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-3 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <Icon name="FileCheck" size={32} className="text-purple-600" />
              <div className="text-left">
                <p className="font-semibold text-purple-900">{selectedFile?.name}</p>
                <p className="text-sm text-purple-700">
                  {(selectedFile?.size / 1024)?.toFixed(2)} KB
                </p>
              </div>
            </div>
            
            {isUploading && (
              <div className="space-y-2">
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-purple-600 to-pink-500 h-full transition-all duration-300 rounded-full"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-600">
                  Uploading... {uploadProgress}%
                </p>
              </div>
            )}
            
            <div className="flex gap-3 justify-center">
              <Button
                onClick={handleUpload}
                disabled={isUploading}
                className="bg-gradient-to-r from-purple-600 to-pink-500 text-white"
              >
                <Icon name="Upload" size={18} />
                <span>{isUploading ? 'Uploading...' : 'Upload File'}</span>
              </Button>
              <Button
                onClick={() => setSelectedFile(null)}
                variant="outline"
                disabled={isUploading}
              >
                <Icon name="X" size={18} />
                <span>Remove</span>
              </Button>
            </div>
          </div>
        ) : (
          <>
            <Icon name="Users" size={64} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Upload Transporter Company Data
            </h3>
            <p className="text-gray-600 mb-4">
              Drag and drop your CSV or Excel file here, or click to browse
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef?.current?.click()}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Icon name="FolderOpen" size={18} />
              <span>Select File</span>
            </Button>
            <p className="text-xs text-gray-500 mt-3">
              Supported formats: CSV, XLSX • Maximum size: 10MB
            </p>
          </>
        )}
      </div>

      {/* Validation Errors */}
      {validationErrors?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-red-900 mb-2">Validation Errors</h4>
              <ul className="space-y-1">
                {validationErrors?.map((error, index) => (
                  <li key={index} className="text-sm text-red-700">
                    • {error}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Field Reference */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Icon name="List" size={18} className="text-purple-600" />
          Template Field Reference
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templateFields?.map((field, index) => (
            <div key={index} className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">
                    {field?.field}
                    {field?.required && <span className="text-red-500 ml-1">*</span>}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Example: {field?.example}
                  </p>
                </div>
                {field?.required && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                    Required
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Info */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-purple-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-purple-800">
            <p className="font-semibold mb-2">Important Notes:</p>
            <ul className="space-y-1 ml-4 list-disc">
              <li>All fields marked with * are mandatory</li>
              <li>GST and PAN numbers will be validated for authenticity</li>
              <li>Service areas should be comma-separated state names</li>
              <li>Insurance expiry dates should be in YYYY-MM-DD format</li>
              <li>Bank account details will be verified</li>
              <li>System will perform duplicate company registration checks</li>
              <li>Verification documents can be added later if URLs are not available</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransporterRegistrationUpload;